import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

const navItems = [
  { id: 'services', label: 'Services' },
  { id: 'projects', label: 'Projects' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'stack', label: 'Stack' },
  { id: 'studio', label: 'Studio' },
  { id: 'resume', label: 'Experience' },
  { id: 'blog', label: 'Blog' },
  { id: 'contact', label: 'Contact' },
];

const Header: React.FC = () => {
  const { themeConfig } = useTheme();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === '/';

  const hrefFor = (id: string) => (onHome ? `#${id}` : `/#${id}`);

  return (
    <header
      className={`${themeConfig.layout?.headerStyle} border-b border-canvas-border/60 bg-canvas-base/70`}
    >
      <div
        className={`${themeConfig.layout?.containerStyle} flex items-center justify-between`}
      >
        {/* Brand */}
        <Link to="/" className="group flex items-center gap-3">
          <span className="relative grid h-10 w-10 place-items-center rounded-xl border border-neon-cyan/40 bg-canvas-node shadow-neon-cyan">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="9" width="7" height="6" rx="2" fill="#22e6ff" fillOpacity="0.25" stroke="#22e6ff" strokeWidth="1.3" />
              <rect x="14" y="4" width="7" height="6" rx="2" fill="#ff3df0" fillOpacity="0.2" stroke="#ff3df0" strokeWidth="1.3" />
              <rect x="14" y="14" width="7" height="6" rx="2" fill="#2bffb0" fillOpacity="0.2" stroke="#2bffb0" strokeWidth="1.3" />
              <path d="M10 12 H12.5 C13 12 13 7 14 7" stroke="#22e6ff" strokeWidth="1.3" />
              <path d="M10 12 H12.5 C13 12 13 17 14 17" stroke="#2bffb0" strokeWidth="1.3" />
            </svg>
          </span>
          <span className="leading-tight">
            <span className="block font-display text-lg font-bold text-ink-main">
              Chaitanya Sharma
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-neon-cyan">
              ai &amp; full-stack dev
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-4 lg:flex xl:gap-6">
          {navItems.map((item) => (
            <a
              key={item.id}
              href={hrefFor(item.id)}
              className="font-mono text-sm text-ink-soft transition-colors hover:text-neon-cyan"
            >
              {item.label}
            </a>
          ))}
          <a
            href="https://calendly.com/sharmachaitanya945/meeting-with-me"
            target="_blank"
            rel="noopener noreferrer"
            className="neon-btn !px-4 !py-2 text-sm"
          >
            Book a call
          </a>
        </nav>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="grid h-10 w-10 place-items-center rounded-lg border border-canvas-border text-ink-soft lg:hidden"
          aria-label="Toggle menu"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d={open ? 'M6 18L18 6M6 6l12 12' : 'M4 7h16M4 12h16M4 17h16'}
            />
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-canvas-border/60 bg-canvas-base/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={hrefFor(item.id)}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2.5 font-mono text-sm text-ink-soft transition-colors hover:bg-canvas-node hover:text-neon-cyan"
                >
                  {item.label}
                </a>
              ))}
              <a
                href="https://calendly.com/sharmachaitanya945/meeting-with-me"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="neon-btn mt-2 justify-center text-sm"
              >
                Book a call
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
